import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action} from '@ngrx/store';
import {merge, Observable, of} from 'rxjs';
import {catchError, mergeMap} from 'rxjs/operators';
import {CurrentSkedApiService} from '../../api/current-sked-api.service';
import {createCallbackActions, emitErrorActions} from '../store.utils';
import {
  AddSubtaskNoteAction,
  AddSubtaskNoteSuccessAction,
  CurrentSkedActionType,
  CurrentSkedChangeSubtaskStatusAction,
  CurrentSkedChangeTaskStatusAction,
  CurrentSkedChangeTaskStatusSuccessAction,
  CurrentSkedCompleteTaskAction,
  CurrentSkedCompleteTaskSuccessAction,
  CurrentSkedUpdateSubtaskMetricsAction,
  CurrentSkedUpdateSubtaskMetricsSuccessAction,
  GetChallengeCycleDayAction,
  GetChallengeCycleDaySuccessAction,
  GetCSChallengeDayDetailAction,
  GetCSChallengeDayDetailFailureAction,
  GetCSChallengeDayDetailSuccessAction,
  GetCSChallengesCycleAction,
  GetCSChallengesCycleSuccessAction,
  SaveChallengeMetricValuesAction,
  SaveChallengeMetricValuesSuccessAction,
  GetCurrentSkedAction,
  GetCurrentSkedChallengeAction,
  GetCurrentSkedChallengeSuccessAction,
  GetCurrentSkedProjectAction,
  GetCurrentSkedProjectSuccessAction,
  GetCurrentSkedSuccessAction,
  GetCurrentSkedTaskAction,
  GetCurrentSkedTaskSuccessAction,
  UpdateCurrentSked,
  UpdateCurrentSkedSuccess,
} from './current-sked.action';

@Injectable()
export class CurrentSkedEffects {
  public get$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCurrentSkedAction>(CurrentSkedActionType.GET),
      mergeMap(action => {
        const {expired, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.get(expired).pipe(
          mergeMap(response => [
            new GetCurrentSkedSuccessAction({
              sked: response.userSkedWithTasks,
              teamSked: response.teamSkedWithTasks,
              expired,
            }),
            ...createCallbackActions(onSuccess),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public getProject$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCurrentSkedProjectAction>(CurrentSkedActionType.GET_PROJECT),
      mergeMap(action => {
        const {projectId, expired, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.getProject(projectId, expired).pipe(
          mergeMap(skedProject => [
            new GetCurrentSkedProjectSuccessAction({skedProject, expired}),
            ...createCallbackActions(onSuccess),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public getTask$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCurrentSkedTaskAction>(CurrentSkedActionType.GET_TASK),
      mergeMap(action => {
        const {taskId, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.getCurrentSkedTask(taskId).pipe(
          mergeMap(task => [new GetCurrentSkedTaskSuccessAction({task}), ...createCallbackActions(onSuccess, task)]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public getChallenge$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCurrentSkedChallengeAction>(CurrentSkedActionType.GET_CHALLENGE),
      mergeMap(action => {
        const {challengeId, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.getCurrentSkedChallenge(challengeId).pipe(
          mergeMap(csChallengeData => [
            new GetCurrentSkedChallengeSuccessAction({csChallengeData}),
            ...createCallbackActions(onSuccess, csChallengeData.challenge),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public getCycleDay$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetChallengeCycleDayAction>(CurrentSkedActionType.GET_CYCLE_DAY),
      mergeMap(action => {
        const {challengeId, timestamp, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.getChallengeCycleDay(challengeId, timestamp).pipe(
          mergeMap(csChallengeData => [
            new GetChallengeCycleDaySuccessAction({csChallengeData}),
            ...createCallbackActions(onSuccess, csChallengeData),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public getChallengeCycle$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCSChallengesCycleAction>(CurrentSkedActionType.GET_CHALLENGE_CYCLE),
      mergeMap(action => {
        const {projectId, date, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.getChallengeCycleByDate(projectId, date).pipe(
          mergeMap(cycle => [
            new GetCSChallengesCycleSuccessAction({projectId, cycle}),
            ...createCallbackActions(onSuccess, cycle),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public getChallengeDayDetail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCSChallengeDayDetailAction>(CurrentSkedActionType.GET_CS_CHALLENGE_DAY_DETAIL),
      mergeMap(action => {
        const {projectId, dateKey, metricId, timestamp, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.getChallengeDayDetail(projectId, timestamp, metricId).pipe(
          mergeMap(chartDetail => [
            new GetCSChallengeDayDetailSuccessAction({chartDetail}),
            ...createCallbackActions(onSuccess, chartDetail),
          ]),
          catchError(error =>
            merge(emitErrorActions(error, onFailure), of(new GetCSChallengeDayDetailFailureAction({dateKey, metricId})))
          )
        );
      })
    )
  );

  public saveChallengeMetricValues$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveChallengeMetricValuesAction>(CurrentSkedActionType.SAVE_CHALLENGE_METRIC_VALUES),
      mergeMap(action => {
        const {projectId, updates, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.saveChallengeMetricValues(projectId, updates).pipe(
          mergeMap(csChallengeData => [
            new SaveChallengeMetricValuesSuccessAction({csChallengeData}),
            ...createCallbackActions(onSuccess),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public changeTaskStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CurrentSkedChangeTaskStatusAction>(CurrentSkedActionType.CHANGE_TASK_STATUS),
      mergeMap(action => {
        const {id, actionType, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.changeTaskStatus(id, actionType).pipe(
          mergeMap(task => [
            new CurrentSkedChangeTaskStatusSuccessAction({task}),
            ...createCallbackActions(onSuccess, task),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public completeTask$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CurrentSkedCompleteTaskAction>(CurrentSkedActionType.COMPLETE_TASK),
      mergeMap(action => {
        const {taskId, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.completeTask(taskId).pipe(
          mergeMap(response => [
            new CurrentSkedCompleteTaskSuccessAction({task: response.task}),
            ...createCallbackActions(onSuccess, {task: response.task, activities: response.activities}),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public changeSubtaskStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CurrentSkedChangeSubtaskStatusAction>(CurrentSkedActionType.CHANGE_SUBTASK_STATUS),
      mergeMap(action => {
        const {taskId, subtaskId, complete, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.changeSubTaskStatus(taskId, subtaskId, complete).pipe(
          mergeMap(response => [
            new CurrentSkedChangeTaskStatusSuccessAction({task: response.task}),
            ...createCallbackActions(onSuccess, {task: response.task, activities: response.activities}),
          ]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public updateSubtaskMetric$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CurrentSkedUpdateSubtaskMetricsAction>(CurrentSkedActionType.UPDATE_SUBTASK_METRICS),
      mergeMap(action => {
        const {trackerId, taskId, metrics, subtaskId, timestamp, onSuccess, onFailure, taskExpired} = action.payload;

        return this.currentSkedApiService
          .updateSubtaskMetricValue(trackerId, taskId, metrics, subtaskId, timestamp, taskExpired)
          .pipe(
            mergeMap(response => [
              new CurrentSkedUpdateSubtaskMetricsSuccessAction({task: response.task}),
              ...createCallbackActions(onSuccess, {task: response.task, activities: response.activities}),
            ]),
            catchError(error => emitErrorActions(error, onFailure))
          );
      })
    )
  );

  public updateCurrentSked$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateCurrentSked>(CurrentSkedActionType.UPDATE_CURRENT_SKED),
      mergeMap(action => {
        const {sked, onSuccess} = action.payload;
        return this.currentSkedApiService.updateCurrentSked(sked).pipe(
          mergeMap(response => {
            return [new UpdateCurrentSkedSuccess({sked: response}), ...createCallbackActions(onSuccess, response)];
          })
        );
      })
    )
  );

  public addSubtaskNote$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<AddSubtaskNoteAction>(CurrentSkedActionType.ADD_SUBTASK_NOTE),
      mergeMap(action => {
        const {subtaskNote, onSuccess, onFailure} = action.payload;

        return this.currentSkedApiService.addSubtaskNote(subtaskNote).pipe(
          mergeMap(task => [new AddSubtaskNoteSuccessAction({task}), ...createCallbackActions(onSuccess, task)]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private currentSkedApiService: CurrentSkedApiService
  ) {}
}
